// Developer: Nithin | Date: 2026-07-26 | UI reorder — Driver/Truck at top, Billed To / Shipped To, Dispatch No + Invoice No; added new fields
// Developer: Nithin | Date: 2026-07-26 | dispatchNo read-only (auto-gen), orderQuotationNo read-only (auto-fill), invoiceNumber editable, addresses auto-filled
// Developer: Nithin | Date: 2026-07-26 | dispatchNo editable input, address simplified to 2 textareas, orderQuotationNo auto-filled from sale reference
// Developer: Nithin | Date: 2026-07-26 | Removed (From)/(Consignee) labels, added Same as Billed To checkbox to mirror address
import React, { useState } from "react";
import { Modal, Button, FormInput, FormTextarea } from "@/components/ui";
import { useSalesDispatchLogic } from "../hooks/useSalesDispatchLogic";
import { Truck, User, Hash, FileText, Clock, CheckCircle, Package, Store, MapPin, Download, Phone, Receipt, Tag, Lock } from "lucide-react";

const STATUS_CONFIG = {
  pending_store: {
    label: 'Pending in Store',
    variant: 'bg-amber-50 text-amber-700 border-amber-200',
    icon: Clock,
  },
  received_store: {
    label: 'Received in Store',
    variant: 'bg-blue-50 text-blue-700 border-blue-200',
    icon: Store,
  },
  dispatch_started: {
    label: 'Dispatch Started',
    variant: 'bg-purple-50 text-purple-700 border-purple-200',
    icon: Truck,
  },
  delivered: {
    label: 'Delivered',
    variant: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    icon: CheckCircle,
  },
};

const fmtDateTime = (date) => {
  if (!date) return '—';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const UserBadge = ({ user }) => {
  if (!user) return <span className="text-slate-400 text-[11px]">—</span>;
  const name = user.name || [user.firstName, user.lastName].filter(Boolean).join(' ') || 'Unknown';
  return <span className="text-[12px] font-medium text-slate-700">{name}</span>;
};

/** Read-only auto-generated / auto-filled field chip */
const AutoChip = ({ icon: Icon, label, value, color = 'slate' }) => {
  const colors = {
    slate:  'bg-slate-50 border-slate-200 text-slate-600',
    blue:   'bg-blue-50  border-blue-200  text-blue-700',
    amber:  'bg-amber-50 border-amber-200 text-amber-700',
  };
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1">
        {label}
        <span className="ml-1 text-[9px] font-semibold bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full border border-slate-200 flex items-center gap-0.5">
          <Lock size={8} /> Auto
        </span>
      </span>
      <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border font-mono font-bold text-[12px] tracking-wide ${colors[color] || colors.slate}`}>
        {Icon && <Icon className="w-3.5 h-3.5 shrink-0 opacity-60" />}
        <span>{value || '—'}</span>
      </div>
    </div>
  );
};

const formatTruckNumber = (value) => {
  const raw = value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 10);
  const parts = [];
  if (raw.length > 0) parts.push(raw.slice(0, 2));
  if (raw.length > 2) parts.push(raw.slice(2, 4));
  if (raw.length > 4) parts.push(raw.slice(4, 6));
  if (raw.length > 6) parts.push(raw.slice(6, 10));
  return parts.join('-');
};

// Developer: Nithin | Date: 2026-07-26 | dispatchNo editable input, address simplified to 2 textareas, orderQuotationNo auto-filled from sale reference
const SaleDispatchModal = ({ isOpen, onClose, sale, onRefresh }) => {
  const {
    loading,
    dispatchInfo,
    dispatchStatus,
    driverName, setDriverName,
    truckNumber, setTruckNumber,
    notes, setNotes,
    fromAddress, setFromAddress,
    toAddress, setToAddress,
    dispatchNo, setDispatchNo,
    invoiceNumber, setInvoiceNumber,
    orderQuotationNo,
    handleMarkAsReceived,
    handleStartDispatch,
    handleConfirmDelivery,
    handleDownloadReceipt,
  } = useSalesDispatchLogic({ sale, onRefresh });

  // "Same as Billed To" checkbox — when ticked, Shipped To mirrors Billed To live
  const [sameAddress, setSameAddress] = useState(false);
  const handleSameAddressChange = (checked) => {
    setSameAddress(checked);
    if (checked) setToAddress(fromAddress);
  };
  // Keep Shipped To in sync whenever Billed To changes while checkbox is on
  const handleBilledToChange = (val) => {
    setFromAddress(val);
    if (sameAddress) setToAddress(val);
  };

  const config = STATUS_CONFIG[dispatchStatus] || STATUS_CONFIG.pending_store;
  const StatusIcon = config.icon;
  const items = dispatchInfo?.items || sale?.items || [];

  const renderItemsTable = () => (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      <table className="w-full text-left text-[12px]">
        <thead className="bg-slate-50 text-slate-600 border-b border-slate-200">
          <tr>
            <th className="px-3 py-2 font-medium">#</th>
            <th className="px-3 py-2 font-medium">Item</th>
            <th className="px-3 py-2 font-medium text-center">Qty</th>
            <th className="px-3 py-2 font-medium text-right">Rate</th>
            <th className="px-3 py-2 font-medium text-right">Amount</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {items.length === 0 ? (
            <tr>
              <td colSpan={5} className="px-3 py-4 text-center text-slate-400 text-[11px]">
                No items found
              </td>
            </tr>
          ) : items.map((item, idx) => (
            <tr key={idx} className="bg-white">
              <td className="px-3 py-2 text-slate-500">{idx + 1}</td>
              <td className="px-3 py-2 font-medium text-slate-700">{item.itemName || 'Unnamed'}</td>
              <td className="px-3 py-2 text-center text-slate-600">{item.qty || 0}</td>
              <td className="px-3 py-2 text-right text-slate-600">₹{Number(item.rate || 0).toLocaleString('en-IN')}</td>
              <td className="px-3 py-2 text-right font-medium text-slate-700">₹{Number(item.amount || 0).toLocaleString('en-IN')}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderPendingStore = () => (
    <div className="flex flex-col gap-4">
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
        <div className="w-9 h-9 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
          <Package size={18} className="text-amber-600" />
        </div>
        <div>
          <h4 className="text-[13px] font-bold text-amber-800">Items Not Yet Received in Store</h4>
          <p className="text-[11px] text-amber-700 mt-1 leading-relaxed">
            The required items for this sale have not been received in the store yet.
            Dispatch cannot be started until the status changes to "Received in Store".
            Click the button below once the items have arrived.
          </p>
        </div>
      </div>
      {renderItemsTable()}
      <Button
        variant="orange"
        size="md"
        fullWidth
        leftIcon={<Store size={16} />}
        onClick={handleMarkAsReceived}
        loading={loading}
      >
        Mark as Received in Store
      </Button>
    </div>
  );

  const renderReceivedStore = () => (
    <div className="flex flex-col gap-4">
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
        <div className="w-9 h-9 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
          <Store size={18} className="text-blue-600" />
        </div>
        <div>
          <h4 className="text-[13px] font-bold text-blue-800">Items Received — Ready for Dispatch</h4>
          <p className="text-[11px] text-blue-700 mt-1 leading-relaxed">
            Fill in the delivery details below and start the dispatch process.
          </p>
        </div>
      </div>

      {/* 1 ── Driver Name + Truck Number */}
      <div className="grid grid-cols-2 gap-3">
        <FormInput
          label="Driver Name"
          required
          placeholder="Enter driver full name"
          value={driverName}
          onChange={(e) => setDriverName(e.target.value)}
          leftIcon={<User className="w-3.5 h-3.5 text-slate-400" />}
        />
        <FormInput
          label="Truck Number"
          required
          placeholder="AB-12-CD-3456"
          value={truckNumber}
          onChange={(e) => setTruckNumber(formatTruckNumber(e.target.value))}
          leftIcon={<Hash className="w-3.5 h-3.5 text-slate-400" />}
          inputClassName="uppercase font-bold tracking-wider"
        />
      </div>

      {/* 2 ── Billed To / Shipped To */}
      <div className="grid grid-cols-2 gap-3">
        {/* Billed To */}
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
          <h6 className="text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-2 flex items-center gap-1.5">
            <MapPin size={12} className="text-slate-400" />
            Billed To
          </h6>
          <FormTextarea
            placeholder="Store / Warehouse address"
            value={fromAddress}
            onChange={(e) => handleBilledToChange(e.target.value)}
            rows={3}
          />
        </div>
        {/* Shipped To */}
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <h6 className="text-[11px] font-bold text-slate-600 uppercase tracking-wider flex items-center gap-1.5">
              <MapPin size={12} className="text-slate-400" />
              Shipped To
            </h6>
            {/* Same as Billed To checkbox */}
            <label className="flex items-center gap-1.5 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={sameAddress}
                onChange={(e) => handleSameAddressChange(e.target.checked)}
                className="w-3.5 h-3.5 rounded border-slate-300 accent-blue-600 cursor-pointer"
              />
              <span className="text-[10px] font-semibold text-slate-500">Same as Billed To</span>
            </label>
          </div>
          <FormTextarea
            placeholder="Customer / Delivery address"
            value={toAddress}
            onChange={(e) => !sameAddress && setToAddress(e.target.value)}
            rows={3}
            disabled={sameAddress}
            className={sameAddress ? "opacity-60 cursor-not-allowed" : ""}
          />
        </div>
      </div>

      {/* 3 ── Dispatch No (editable) + Invoice Number (editable) + Order/Quote No (auto-filled read-only) */}
      <div className="grid grid-cols-3 gap-3">
        <FormInput
          label="Dispatch No"
          placeholder="e.g. DSP-001"
          value={dispatchNo}
          onChange={(e) => setDispatchNo(e.target.value)}
          leftIcon={<Receipt className="w-3.5 h-3.5 text-slate-400" />}
        />
        <FormInput
          label="Invoice Number"
          placeholder="Enter invoice number"
          value={invoiceNumber}
          onChange={(e) => setInvoiceNumber(e.target.value)}
          leftIcon={<Tag className="w-3.5 h-3.5 text-slate-400" />}
        />
        <AutoChip
          icon={FileText}
          label="Order / Quotation No"
          value={orderQuotationNo}
          color="amber"
        />
      </div>

      {/* 4 ── Items Table */}
      {renderItemsTable()}

      {/* 5 ── Notes */}
      <FormTextarea
        label="Notes / Description"
        placeholder="Any additional delivery instructions..."
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        rows={2}
      />
      <Button
        variant="orange"
        size="md"
        fullWidth
        leftIcon={<Truck size={16} />}
        onClick={handleStartDispatch}
        loading={loading}
      >
        Start Dispatch
      </Button>
    </div>
  );

  const renderDispatchStarted = () => (
    <div className="flex flex-col gap-4">
      <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 flex items-start gap-3">
        <div className="w-9 h-9 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
          <Truck size={18} className="text-purple-600" />
        </div>
        <div>
          <h4 className="text-[13px] font-bold text-purple-800">Dispatch In Progress</h4>
          <p className="text-[11px] text-purple-700 mt-1 leading-relaxed">
            The items have been dispatched. Confirm delivery once the customer receives them.
          </p>
        </div>
      </div>
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
        <h5 className="text-[12px] font-bold text-slate-700 uppercase tracking-wider flex items-center gap-2">
          <Truck size={14} className="text-slate-400" />
          Dispatch Details
        </h5>
        <div className="grid grid-cols-2 gap-3 text-[12px]">
          <div>
            <span className="text-slate-500 block text-[10px] uppercase tracking-wide">Driver Name</span>
            <span className="font-medium text-slate-800">{dispatchInfo?.driverName || '—'}</span>
          </div>
          <div>
            <span className="text-slate-500 block text-[10px] uppercase tracking-wide">Truck Number</span>
            <span className="font-medium text-slate-800 uppercase">{dispatchInfo?.truckNumber || '—'}</span>
          </div>
          {(dispatchInfo?.fromAddress || dispatchInfo?.toAddress) && (
            <>
              <div className="col-span-2 border-t border-slate-200 pt-3 mt-1">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <span className="text-slate-500 block text-[10px] uppercase tracking-wide flex items-center gap-1">
                      <MapPin size={10} /> From (Dispatch)
                    </span>
                    <span className="font-medium text-slate-800 text-[11px] whitespace-pre-line">{dispatchInfo?.fromAddress || '—'}</span>
                  </div>
                  <div>
                    <span className="text-slate-500 block text-[10px] uppercase tracking-wide flex items-center gap-1">
                      <MapPin size={10} /> To (Consignee)
                    </span>
                    {dispatchInfo?.toContactPerson && <span className="font-medium text-slate-800 text-[11px]">{dispatchInfo.toContactPerson}</span>}
                    <span className="font-medium text-slate-800 text-[11px] whitespace-pre-line block">{dispatchInfo?.toAddress || '—'}</span>
                    {dispatchInfo?.toPhone && <span className="text-slate-500 text-[10px]">Phone: {dispatchInfo.toPhone}</span>}
                  </div>
                </div>
              </div>
            </>
          )}
          {dispatchInfo?.dispatchNotes && (
            <div className="col-span-2">
              <span className="text-slate-500 block text-[10px] uppercase tracking-wide">Notes</span>
              <span className="font-medium text-slate-800">{dispatchInfo.dispatchNotes}</span>
            </div>
          )}
          <div>
            <span className="text-slate-500 block text-[10px] uppercase tracking-wide">Started At</span>
            <span className="font-medium text-slate-800">{fmtDateTime(dispatchInfo?.dispatchStartedAt)}</span>
          </div>
          <div>
            <span className="text-slate-500 block text-[10px] uppercase tracking-wide">Started By</span>
            <UserBadge user={dispatchInfo?.dispatchStartedBy} />
          </div>
        </div>
      </div>
      {renderItemsTable()}
      <Button
        variant="orange"
        size="md"
        fullWidth
        leftIcon={<CheckCircle size={16} />}
        onClick={handleConfirmDelivery}
        loading={loading}
      >
        Confirm Delivery
      </Button>
    </div>
  );

  const renderDelivered = () => (
    <div className="flex flex-col gap-4">
      <Button variant="orange" size="md" fullWidth leftIcon={<Download size={16} />} onClick={handleDownloadReceipt}>Download Delivery Receipt</Button>
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-start gap-3">
        <div className="w-9 h-9 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
          <CheckCircle size={18} className="text-emerald-600" />
        </div>
        <div>
          <h4 className="text-[13px] font-bold text-emerald-800">Delivery Completed</h4>
          <p className="text-[11px] text-emerald-700 mt-1 leading-relaxed">
            This sale has been delivered successfully. The dispatch history is shown below.
          </p>
        </div>
      </div>
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
        <h5 className="text-[12px] font-bold text-slate-700 uppercase tracking-wider flex items-center gap-2">
          <Truck size={14} className="text-slate-400" />
          Dispatch History
        </h5>
        <div className="grid grid-cols-2 gap-3 text-[12px]">
          <div>
            <span className="text-slate-500 block text-[10px] uppercase tracking-wide">Driver Name</span>
            <span className="font-medium text-slate-800">{dispatchInfo?.driverName || '—'}</span>
          </div>
          <div>
            <span className="text-slate-500 block text-[10px] uppercase tracking-wide">Truck Number</span>
            <span className="font-medium text-slate-800 uppercase">{dispatchInfo?.truckNumber || '—'}</span>
          </div>
          {(dispatchInfo?.fromAddress || dispatchInfo?.toAddress) && (
            <>
              <div className="col-span-2 border-t border-slate-200 pt-3 mt-1">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <span className="text-slate-500 block text-[10px] uppercase tracking-wide flex items-center gap-1">
                      <MapPin size={10} /> From (Dispatch)
                    </span>
                    <span className="font-medium text-slate-800 text-[11px] whitespace-pre-line">{dispatchInfo?.fromAddress || '—'}</span>
                  </div>
                  <div>
                    <span className="text-slate-500 block text-[10px] uppercase tracking-wide flex items-center gap-1">
                      <MapPin size={10} /> To (Consignee)
                    </span>
                    {dispatchInfo?.toContactPerson && <span className="font-medium text-slate-800 text-[11px]">{dispatchInfo.toContactPerson}</span>}
                    <span className="font-medium text-slate-800 text-[11px] whitespace-pre-line block">{dispatchInfo?.toAddress || '—'}</span>
                    {dispatchInfo?.toPhone && <span className="text-slate-500 text-[10px]">Phone: {dispatchInfo.toPhone}</span>}
                  </div>
                </div>
              </div>
            </>
          )}
          {dispatchInfo?.dispatchNotes && (
            <div className="col-span-2">
              <span className="text-slate-500 block text-[10px] uppercase tracking-wide">Notes</span>
              <span className="font-medium text-slate-800">{dispatchInfo.dispatchNotes}</span>
            </div>
          )}
        </div>
        <div className="border-t border-slate-200 pt-3 mt-1">
          <h6 className="text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-2 flex items-center gap-1.5">
            <Clock size={12} /> Timeline
          </h6>
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-[11px]">
              <div className="w-2 h-2 rounded-full bg-blue-400 flex-shrink-0" />
              <span className="text-slate-500">Dispatch Started:</span>
              <span className="font-medium text-slate-700">{fmtDateTime(dispatchInfo?.dispatchStartedAt)}</span>
              <span className="text-slate-400">by</span>
              <UserBadge user={dispatchInfo?.dispatchStartedBy} />
            </div>
            <div className="flex items-center gap-2 text-[11px]">
              <div className="w-2 h-2 rounded-full bg-emerald-400 flex-shrink-0" />
              <span className="text-slate-500">Delivery Confirmed:</span>
              <span className="font-medium text-slate-700">{fmtDateTime(dispatchInfo?.deliveryConfirmedAt)}</span>
              <span className="text-slate-400">by</span>
              <UserBadge user={dispatchInfo?.deliveryConfirmedBy} />
            </div>
          </div>
        </div>
      </div>
      {renderItemsTable()}
    </div>
  );

  const renderContent = () => {
    switch (dispatchStatus) {
      case 'pending_store': return renderPendingStore();
      case 'received_store': return renderReceivedStore();
      case 'dispatch_started': return renderDispatchStarted();
      case 'delivered': return renderDelivered();
      default: return renderPendingStore();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Dispatch Management"
      size="lg"
      bodyClassName="overflow-y-auto flex-1 px-4 pb-4 pt-2 sm:px-5 sm:pb-3 sm:pt-2"
      footer={
        <div className="flex flex-col sm:flex-row items-center gap-3 w-full">
          <Button
            type="button"
            variant="secondary"
            className="w-full sm:flex-1 h-10 rounded-lg text-[12px] font-bold"
            onClick={onClose}
          >
            {dispatchStatus === 'delivered' ? 'Close' : 'Cancel'}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4 font-poppins">
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${config.variant.split(' ')[0]}`}>
            <StatusIcon size={16} className={config.variant.split(' ')[1]} />
          </div>
          <div className="flex items-center gap-2">
            <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-semibold border ${config.variant}`}>
              {config.label}
            </span>
            {sale?.invoiceNo && sale?.invoiceNo !== '—' && (
              <span className="text-[11px] text-slate-400 font-mono">#{sale.invoiceNo}</span>
            )}
          </div>
        </div>
        {renderContent()}
      </div>
    </Modal>
  );
};

export default SaleDispatchModal;
